import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import PushDebugOverlay from "./components/PushDebugOverlay";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider } from "./lib/i18n";
import { CartProvider } from "./lib/cart";
import { NetworkProvider, useNetwork } from "./lib/network";
import ErrorBoundary from "./components/ErrorBoundary";
import NoInternetScreen from "./components/NoInternetScreen";
import ForceUpdateScreen from "./components/ForceUpdateScreen";
import TenantTheme from "./lib/TenantTheme";
import { configureStatusBar } from "./lib/statusBar";
import { hideBootSplash, whenSplashImageReady } from "./lib/bootSplash";
import { useAppVersion } from "./lib/deviceInfo";
import { getNativePlatform, getPersona, getRequiredMinVersion, getStoreUrl, isVersionBelow } from "./lib/forceUpdate";

import Landing from "./pages/Landing";
import StartStore from "./pages/StartStore";
import CustomerLogin from "./pages/CustomerLogin";
import DeliveryLogin from "./pages/DeliveryLogin";
import AdminLogin from "./pages/AdminLogin";

import CustomerLayout from "./pages/customer/CustomerLayout";
import CustomerHome from "./pages/customer/CustomerHome";
import Products from "./pages/customer/Products";
import ProductDetail from "./pages/customer/ProductDetail";
import Subscriptions from "./pages/customer/Subscriptions";
import Wallet from "./pages/customer/Wallet";
import Profile from "./pages/customer/Profile";
import { Orders, OrderTrack } from "./pages/customer/Orders";
import Reels from "./pages/customer/Reels";
import Checkout from "./pages/customer/Checkout";

import RiderToday from "./pages/delivery/RiderToday";
import RiderProfile from "./pages/delivery/RiderProfile";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { AdminCustomers, AdminProducts, AdminSubscriptions } from "./pages/admin/AdminCRUD";
import { AdminDeliveries, AdminRoutes, AdminBottles, AdminReels, AdminReports, AdminCustomerDiscounts } from "./pages/admin/AdminOps";
import CustomerBottleHistory from "./pages/admin/CustomerBottleHistory";
import CustomerTransactions from "./pages/admin/CustomerTransactions";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminPwa from "./pages/admin/AdminPwa";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminApartments from "./pages/admin/AdminApartments";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminWallet from "./pages/admin/AdminWallet";
import AdminPlan from "./pages/admin/AdminPlan";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminLoyalty from "./pages/admin/AdminLoyalty";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminTrials from "./pages/admin/AdminTrials";
import Loyalty from "./pages/customer/Loyalty";
import Referrals from "./pages/customer/Referrals";
import ManageAddresses from "./pages/customer/ManageAddresses";
import NextDelivery from "./pages/customer/NextDelivery";
import MyBills from "./pages/customer/MyBills";
import ModulePlaceholder from "./pages/admin/ModulePlaceholder";
import { Store, Heart, Gift, Brain } from "lucide-react";

import { SuperDashboard, SuperTenants, SuperPlans } from "./pages/super/SuperPages";
import SuperSettings from "./pages/super/SuperSettings";
import SuperTenantWallets from "./pages/super/SuperTenantWallets";
import SuperPush from "./pages/super/SuperPush";
import SuperMigrate from "./pages/super/SuperMigrate";
import SuperImportCustomers from "./pages/super/SuperImportCustomers";
import TenantRedirect from "./pages/TenantRedirect";
import LegalPage from "./pages/LegalPage";
import { SellerLogin, SellerDashboard } from "./pages/seller/Seller";
import PushBridge from "./components/PushBridge";

// Root: customers (incl. guests) land on storefront; other roles route to their app
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/app/home" replace />;
  if (user.role === "customer") return <Navigate to="/app/home" replace />;
  if (user.role === "delivery_boy") return <Navigate to="/rider/today" replace />;
  if (user.role === "tenant_admin") return <Navigate to="/admin/dashboard" replace />;
  if (user.role === "super_admin") return <Navigate to="/super/dashboard" replace />;
  if (user.role === "seller") return <Navigate to="/seller/dashboard" replace />;
  return <Navigate to="/app/home" replace />;
}

// Mounts TenantTheme app-wide so the document title, theme color, favicon and
// brand-coloured CSS variables update on every screen (admin, super, rider…).
function GlobalTenantTheme() {
  const { tenant } = useAuth();
  return <TenantTheme tenant={tenant} />;
}

// Reset window scroll to top on every route change. Without this, a long
// page navigation lands the user wherever they were on the previous page.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

// Hands the boot splash (native launch screen + the index.html overlay, see
// lib/bootSplash.js) over to the app once its first real screen can render:
// the connectivity check and session check are done and the tenant has
// loaded — or, if the tenant fetch is slow/failing, after a short grace so it
// can never hold the splash. Renders nothing; hideBootSplash is idempotent.
const TENANT_GRACE_MS = 1200;
function BootSplashHandoff() {
  const { loading, tenant } = useAuth();
  const { online, checking } = useNetwork();
  const [graceOver, setGraceOver] = useState(false);
  const settled = !checking && (!online || !loading);
  useEffect(() => {
    if (!settled || tenant) return undefined;
    const t = setTimeout(() => setGraceOver(true), TENANT_GRACE_MS);
    return () => clearTimeout(t);
  }, [settled, tenant]);
  useEffect(() => {
    if (!(settled && (tenant || graceOver || !online))) return undefined;
    let cancelled = false;
    // Don't drop the native launch screen just because React is ready: it is
    // showing this tenant's splash artwork, and on the first launch after an
    // install nothing is cached yet, so there is no web overlay to replace
    // it with. Wait until the artwork itself is ready (capped inside
    // whenSplashImageReady) and hand off in one step. Tenants without a
    // splash image resolve immediately — unchanged behaviour.
    whenSplashImageReady(tenant?.branding).then(() => {
      if (!cancelled) hideBootSplash();
    });
    return () => { cancelled = true; };
  }, [settled, tenant, graceOver, online]);
  return null;
}

// Gates the routed app content on connectivity. Sits inside AuthProvider/
// CartProvider (not above them) so an offline blip never wipes the user's
// session or cart — only the routed content unmounts/remounts.
//   - checking: the one-time launch-time connectivity check hasn't
//     resolved yet — render nothing that could fire an API call.
//   - !online: connectivity confirmed lost — show the dedicated screen.
//   - online: key the content on reconnectCount so a false→true transition
//     forces a full remount, which re-runs every route's own data-loading
//     effects ("retry the required API calls") with no manual restart.
function NetworkGate({ children }) {
  const { online, checking, reconnectCount } = useNetwork();
  const { refresh } = useAuth();
  const prevReconnect = useRef(reconnectCount);

  useEffect(() => {
    if (reconnectCount > prevReconnect.current) refresh();
    prevReconnect.current = reconnectCount;
  }, [reconnectCount, refresh]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  }
  if (!online) return <NoInternetScreen />;
  return <React.Fragment key={reconnectCount}>{children}</React.Fragment>;
}

// Gates the routed app content on the native app's minimum required version
// for this tenant + platform + persona. No-ops entirely on web (no app
// store there) and while the tenant/version haven't loaded yet — only ever
// blocks once we can positively confirm the installed build is too old, so
// a slow/failed check never wrongly locks a user out.
function ForceUpdateGate({ children }) {
  const { tenant } = useAuth();
  const { pathname } = useLocation();
  const appVersion = useAppVersion();

  if (appVersion.source === "native" && appVersion.ready && tenant) {
    const platform = getNativePlatform();
    const persona = getPersona(pathname);
    const required = getRequiredMinVersion(tenant, platform, persona);
    if (isVersionBelow(appVersion.version, required)) {
      const storeUrl = getStoreUrl(tenant, platform, persona, appVersion.appId);
      const appName = persona === "rider" ? tenant.rider_app_name : tenant.customer_app_name;
      return <ForceUpdateScreen storeUrl={storeUrl} appName={appName || tenant.name} />;
    }
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      {/* offset/mobileOffset push the toast stack below the status bar /
          notch / Dynamic Island — sonner has no built-in safe-area
          awareness, so without this a top-center toast paints flush
          against y=0 and gets clipped behind system UI on native. Same
          env(safe-area-inset-top) already used by every sticky header
          (see lib/index.css .pt-safe) — falls back to 0px on web/older
          Android where the var is unset, so desktop spacing is unchanged.
          mobileOffset covers sonner's own <600px "mobile" breakpoint,
          which every phone screen (web or native) falls under. */}
      <Toaster
        position="top-center"
        richColors
        closeButton
        offset={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
        mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }}
      />
      <PushDebugOverlay />
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
            <ErrorBoundary>
            <NetworkProvider>
            <BootSplashHandoff />
            <NetworkGate>
            <ForceUpdateGate>
            <GlobalTenantTheme />
            <ScrollToTop />
            {/* Surfaces foreground pushes and handles notification taps.
                Inside the router because it navigates. Renders nothing. */}
            <PushBridge />
            <Routes>
              {/* Root → customer view */}
              <Route path="/" element={<RootRedirect/>}/>

              {/* Tenant path-based storefront: localappstore.in/t/{slug} */}
              <Route path="/t/:slug/*" element={<TenantRedirect/>}/>

              {/* Marketing/landing now lives at /about */}
              <Route path="/about" element={<Landing/>}/>

              {/* Start Store - public dairy onboarding application */}
              <Route path="/startstore" element={<StartStore/>}/>

              {/* Logins */}
              <Route path="/login" element={<CustomerLogin/>}/>
              <Route path="/delivery/login" element={<DeliveryLogin/>}/>
              <Route path="/admin/login" element={<AdminLogin/>}/>
              <Route path="/super-admin/login" element={<AdminLogin superAdmin/>}/>
              <Route path="/seller/login" element={<SellerLogin/>}/>

              {/* Legal pages — tenant-scoped, public, no auth.
                  App Store / Play Store reviewers must be able to reach these. */}
              <Route path="/legal/privacy" element={<LegalPage doc="privacy"/>}/>
              <Route path="/legal/terms" element={<LegalPage doc="terms"/>}/>
              <Route path="/legal/:slug/privacy" element={<LegalPage doc="privacy"/>}/>
              <Route path="/legal/:slug/terms" element={<LegalPage doc="terms"/>}/>

              {/* Customer App */}
              <Route path="/app" element={<CustomerLayout/>}>
                <Route index element={<Navigate to="/app/home"/>}/>
                <Route path="home" element={<CustomerHome/>}/>
                <Route path="products" element={<Products/>}/>
                <Route path="products/:id" element={<ProductDetail/>}/>
                <Route path="subscriptions" element={<Subscriptions/>}/>
                <Route path="wallet" element={<Wallet/>}/>
                <Route path="profile" element={<Profile/>}/>
                <Route path="orders" element={<Orders/>}/>
                <Route path="orders/:id" element={<OrderTrack/>}/>
                <Route path="checkout" element={<Checkout/>}/>
                <Route path="reels" element={<Reels/>}/>
                <Route path="loyalty" element={<Loyalty/>}/>
                <Route path="referrals" element={<Referrals/>}/>
                <Route path="addresses" element={<ManageAddresses/>}/>
                <Route path="next-delivery" element={<NextDelivery/>}/>
                <Route path="bills" element={<MyBills/>}/>
              </Route>

              {/* Rider App */}
              <Route path="/rider/today" element={<RiderToday/>}/>
              <Route path="/rider/profile" element={<RiderProfile/>}/>

              {/* Seller portal */}
              <Route path="/seller/dashboard" element={<SellerDashboard/>}/>

              {/* Tenant Admin */}
              <Route path="/admin" element={<AdminLayout/>}>
                <Route index element={<Navigate to="/admin/dashboard"/>}/>
                <Route path="dashboard" element={<AdminDashboard/>}/>
                <Route path="customers" element={<AdminCustomers/>}/>
                <Route path="customers/:id/bottles" element={<CustomerBottleHistory/>}/>
                <Route path="customers/:id/transactions" element={<CustomerTransactions/>}/>
                <Route path="categories" element={<AdminCategories/>}/>
                <Route path="brands" element={<AdminBrands/>}/>
                <Route path="banners" element={<AdminBanners/>}/>
                <Route path="products" element={<AdminProducts/>}/>
                <Route path="subscriptions" element={<AdminSubscriptions/>}/>
                <Route path="deliveries" element={<AdminDeliveries/>}/>
                <Route path="routes" element={<AdminRoutes/>}/>
                <Route path="apartments" element={<AdminApartments/>}/>
                <Route path="bottles" element={<AdminBottles/>}/>
                <Route path="reels" element={<AdminReels/>}/>
                <Route path="reports" element={<AdminReports/>}/>
                <Route path="discounts" element={<AdminCustomerDiscounts/>}/>
                <Route path="orders" element={<AdminOrders/>}/>
                <Route path="wallet" element={<AdminWallet/>}/>
                <Route path="plan" element={<AdminPlan/>}/>
                <Route path="marketplace" element={<ModulePlaceholder testKey="marketplace" title="Multi-Seller Marketplace" description="Let multiple sellers list products under your tenant. Manage commissions, payouts and seller onboarding." icon={Store}/>}/>
                <Route path="loyalty" element={<AdminLoyalty/>}/>
                <Route path="referrals" element={<AdminReferrals/>}/>
                <Route path="trials" element={<AdminTrials/>}/>
                <Route path="ai-insights" element={<ModulePlaceholder testKey="ai-insights" title="AI Insights & Demand Forecasting" description="Predict tomorrow's demand, identify churn risk and surface anomalies — powered by AI." icon={Brain}/>}/>
                <Route path="settings" element={<AdminSettings/>}/>
                <Route path="pwa" element={<AdminPwa/>}/>
              </Route>

              {/* Super Admin */}
              <Route path="/super" element={<AdminLayout superAdmin/>}>
                <Route index element={<Navigate to="/super/dashboard"/>}/>
                <Route path="dashboard" element={<SuperDashboard/>}/>
                <Route path="tenants" element={<SuperTenants/>}/>
                <Route path="wallets" element={<SuperTenantWallets/>}/>
                <Route path="plans" element={<SuperPlans/>}/>
                <Route path="push" element={<SuperPush/>}/>
                <Route path="migrate" element={<SuperMigrate/>}/>
                <Route path="import-customers" element={<SuperImportCustomers/>}/>
                <Route path="settings" element={<SuperSettings/>}/>
              </Route>

              <Route path="*" element={<Navigate to="/"/>}/>
            </Routes>
            </ForceUpdateGate>
            </NetworkGate>
            </NetworkProvider>
            </ErrorBoundary>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}
